'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';
import { useQuery } from '@tanstack/react-query';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import type { VariantsEntity } from '@eliadmin/core/src/api/products/types/product-types';
import type { ProductCardProps } from '../product-card';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { Heading } from '../../ui/heading';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { AddToCartButton } from '../add-to-cart-button';
import { isSellable } from '@eliadmin/core/src/utils/product-utils';
import { useVariantsStore, VariantsProvider } from '@eliadmin/core/src/stores/variant-store';
import { useComparisonStore } from '../../../stores/comparison-store';
import { Fragment, useEffect, useMemo } from 'react';
import { DialogProvider } from '../../ui/dialog';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { Paragraph } from '../../ui/paragraph';
import { LuMinus } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { MdArrowDropDown } from 'react-icons/md';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

function getVariantLabel(variant: VariantsEntity): string {
  const { t } = useTranslations();

  if (variant.name) return variant.name;
  if (variant.values?.length) {
    return variant.values.map(v => v.name ?? v.value).join(' / ');
  }
  return `${t('text3731')} ${variant.id}`;
}

// Unfilled dimensions come from the API as `null` or `0` and rendered as "null cm" / "0 kg".
function formatMeasure(value: string | number | null | undefined, unit?: string | null) {
  if (value == null || value === '' || Number(value) === 0) return '-';
  return unit ? `${value} ${unit}` : `${value}`;
}

function ComparisonProductInternal({ product }: { product: Product }) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const setSelectedVariant = useVariantsStore(state => state.setSelectedVariant);
  const removeProduct = useComparisonStore(s => s.removeProduct);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const currency = generalLayout.currency;
  const currencySettings = generalLayout.currency_settings;
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);

  const variants = useMemo(() => product.variants ?? [], [product.variants]);
  const activeVariant = selectedVariant ?? variants[0];
  const priceDisplay = getProductPriceDisplay(activeVariant ?? product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });

  useEffect(() => {
    if (!selectedVariant && variants[0]) {
      setSelectedVariant(variants[0]);
    }
  }, [selectedVariant, variants, setSelectedVariant]);

  const handleVariantChange = (value: string) => {
    const variant = variants.find(v => String(v.id) === value);
    if (variant) setSelectedVariant(variant);
  };

  const productImage =
    selectedVariant?.images?.[0]?.url ?? product.image?.url ?? '/images/placeholder.png';

  const isDesktop = useBreakpoint('sm');
  const { t } = useTranslations();

  const parameters = [
    {
      id: 1,
      label: `${t('text511')}:`,
      value: formatMeasure(selectedVariant?.width ?? product?.width, 'cm'),
    },
    {
      id: 2,
      label: `${t('text512')}:`,
      value: formatMeasure(selectedVariant?.length ?? product?.length, 'cm'),
    },
    {
      id: 3,
      label: `${t('text513')}:`,
      value: formatMeasure(selectedVariant?.height ?? product?.height, 'cm'),
    },
    {
      id: 5,
      label: `${t('text3748')}:`,
      value: formatMeasure(selectedVariant?.weight ?? product?.weight, product?.weight_unit),
    },
  ];

  const colors = product.attributes?.find(attr => attr.id === 1)?.values;

  const hasColors = colors && colors.length > 0;

  return (
    <div className={cn('relative flex w-full flex-col items-start justify-start gap-4')}>
      <span
        onClick={() => removeProduct(product.id)}
        className="absolute right-3 top-20 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-[4px] bg-walnut-soft text-walnut transition-all ease-in-out hover:opacity-90 sm:h-10 sm:w-10"
      >
        <LuMinus size={20} />
      </span>
      {variants.length > 0 && (
        <Select
          value={activeVariant ? String(activeVariant.id) : undefined}
          onValueChange={handleVariantChange}
        >
          <SelectTrigger
            expandIcon={<MdArrowDropDown size={20} className="text-primary" />}
            className={cn(
              'line-clamp-1 rounded-[4px] border border-border bg-transparent',
              'ea-comparison-product__variant-select-trigger',
            )}
          >
            <SelectValue placeholder={t('text441')} className="line-clamp-1" />
          </SelectTrigger>
          <SelectContent className="w-full max-w-[320px] rounded-[4px] sm:max-w-[332px]">
            {variants.map(variant => (
              <SelectItem
                key={variant.id}
                value={String(variant.id)}
                className="w-full rounded-[3px]"
              >
                {getVariantLabel(variant)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Link
        prefetch={false}
        href={ROUTES.PRODUCT({ slug: product.slug })}
        className="flex w-full flex-col items-start justify-start gap-4"
      >
        <div className="relative aspect-square w-full rounded-[4px] bg-[#F9FAFB] p-[10px] sm:p-3">
          <Image
            src={productImage}
            alt={resolveImageAlt((selectedVariant?.images?.[0]?.url != null ? selectedVariant?.images?.[0] : product?.image), product?.name)}
            fill
            className="absolute inset-0 aspect-square h-full w-full object-contain object-center"
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-3 md:gap-4">
          <Heading
            title={selectedVariant?.name ?? product.name}
            tag="h5"
            className={{
              title: cn('group-hover:text-primary', 'line-clamp-2 transition-all ease-in-out'),
            }}
          />
          {/* <ProductColors product={product} /> */}
        </div>
      </Link>

      <Separator />

      {!priceDisplay.priceOnRequest && (
        <AddToCartButton
          product={product}
          showIcon={true}
          sellable={isSellable(product, 1)}
          quantity={1}
          customText={isDesktop ? t('text227') : t('text1115')}
          className={cn('max-h-12 min-h-12 w-full flex-1', 'ea-product-card__add-to-cart-button')}
          fullSize
        />
      )}

      {selectedVariant?.values && selectedVariant.values.length > 0 && (
        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full items-center justify-between gap-4">
            <Paragraph size="base" className="text-left" color="muted-foreground">
              {t('text164')}:
            </Paragraph>
            <div className="flex flex-col items-end gap-1 text-right">
              {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
                <Paragraph size="sm" color="muted-foreground">
                  {t('text1926')} {formatPrice(priceDisplay.mocPrice, currency, currencySettings)}
                </Paragraph>
              )}
              <Paragraph size="base">
                {priceDisplay.priceOnRequest
                  ? t('text2278')
                  : formatPrice(priceDisplay.current, currency, currencySettings)}
              </Paragraph>
              {priceDisplay.showPreviousPrice && priceDisplay.beforeDiscount != null && (
                <del className="text-sm text-muted-foreground">
                  {formatPrice(priceDisplay.beforeDiscount, currency, currencySettings)}
                </del>
              )}
              {priceDisplay.showMocVocPrices && priceDisplay.secondary != null && (
                <Paragraph size="sm" color="muted-foreground">
                  {t('text1925')} {formatPrice(priceDisplay.secondary, currency, currencySettings)}{' '}
                  {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                </Paragraph>
              )}
            </div>
          </div>
          <Separator />
          <div className="flex w-full items-center justify-between gap-4">
            <Paragraph size="base" className="text-left" color="muted-foreground">
              {t('text237')}
            </Paragraph>
            <Paragraph size="base" className="text-right">
              {selectedVariant?.availability?.name ?? product?.availability?.name}
            </Paragraph>
          </div>
          <Separator />
          {parameters.map(value => (
            <Fragment key={value.label}>
              <div className="flex w-full items-center justify-between gap-4">
                <Paragraph size="base" className="text-left" color="muted-foreground">
                  {value.label}
                </Paragraph>
                <Paragraph size="base" className="text-right">
                  {value.value}
                </Paragraph>
              </div>
              <Separator />
            </Fragment>
          ))}
          {!!hasColors && (
            <div className="flex w-full items-center justify-between gap-4">
              <Paragraph size="base" className="text-left" color="muted-foreground">
                {t('text3749')}:
              </Paragraph>
              <div className="flex items-center justify-end gap-[6px]">
                {colors?.map(color => (
                  <div
                    key={color.id}
                    className={cn(
                      'relative z-10 h-[10px] w-[10px] shrink-0 rounded-full',
                      'ea-product-color',
                    )}
                    style={{ backgroundColor: color.color || '#000' }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ComparisonProduct({ product: storedProduct }: ProductCardProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const me = useCustomerStore(s => s.me);
  const isInitialized = useCustomerStore(s => s.isInitialized);

  const { data: fetchedProduct } = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'detail', storedProduct.id, locale, me?.customer.id ?? null],
    queryFn: () =>
      fetchSlugDetail({ slug: storedProduct.slug, locale, authToken: me?.authToken })
        .then(res => res['App\\Models\\Product'] as unknown as Product)
        .catch(() => null),
    enabled: isInitialized,
  });

  const product = fetchedProduct ?? storedProduct;
  const providerKey = product.id ? `product-${product.id}` : `product-${product.slug}`;

  return (
    <VariantsProvider
      key={`${providerKey}-${fetchedProduct ? 'full' : 'partial'}`}
      state={{ product, selectedQuantity: product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ComparisonProductInternal product={product} />
      </DialogProvider>
    </VariantsProvider>
  );
}
